import * as Yup from 'yup';
import { capitalize, SxProps, Theme } from '@mui/material';
import {
  DataGridPro,
  DataGridProProps,
  GridCallbackDetails,
  GridCellEditStopReasons,
  GridFilterModel,
  GridRenderCellParams,
  GridRowId,
  GridRowModel,
  GridSortModel,
  GridValidRowModel,
  MuiEvent,
  getGridBooleanOperators,
  getGridDateOperators,
  getGridNumericOperators,
  getGridStringOperators,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { FC, createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AnyObjectSchema, ValidationError } from 'yup';
import _ from 'lodash';

import { PhoenixGridHeader } from '@modules/admin/Tools/LenderUtility/shared/PhoenixGridHeader';
import { formatValidation, newGridRecord, newGridRecordId } from '@shared/commonFunctions';
import { useAppSelector } from '@store/hooks';
import { selectGridState, updateGridState } from '@store/reducers/gridsSlice';
import { GridWrapper } from '@styles/PhoenixGrid/GridStyles';

import { AlertModal, IAlertMessage } from '../modals/AlertModal';
import { GridColActions, navigateToLastGridRow } from './GridColActions';
import { dataGridCol, gridBooleanCell } from './common';
import { CustomGridFilterPanel } from './overlays/CustomGridFilterPanel';
import { GridPagination } from './overlays/GridPagination';

export interface IPhoenixGridActions {
  onSave?(row: GridRowModel): Promise<void> | void;
  onCopy?(row: GridRowModel): Promise<void> | void;
  onEdit?(row: GridRowModel): Promise<void> | void;
  onView?(row: GridRowModel): Promise<void> | void;
  onDelete?(row: GridRowModel): Promise<void> | void;
  onCancel?(row: GridRowModel): Promise<void> | void;
}

export interface IPhoenixGridProps extends DataGridProProps {
  name?: string;
  titleColor?: string;
  titleFontWeight?: number;
  validateSchema?: AnyObjectSchema;
  height?: string;
  strict?: boolean;
  withHeader?: boolean;
  subtitle?: string;
  disabled?: boolean;
  canNotEdit?: boolean;
  canNotCopy?: boolean;
  noButton?: boolean;
  buttonTitle?: string;
  pinnedLeft?: string[];
  pinnedRight?: string[];
  actions?: IPhoenixGridActions;
  headerContent?: JSX.Element;
  withSubheader?: JSX.Element;
  withoutConfirmation?: boolean;
  newRowModel?: { [key: string | number | symbol]: any }; // eslint-disable-line
  btn?: FC;
  rowActionsSlot?: FC<GridRowModel>;
  validateValues?: (values: any) => boolean; // eslint-disable-line
  onButtonClick?(): void;
  editableRowId?: string | null;
  isFullHeightColumn?: boolean;
  hideFooter?: boolean;
  isFlexible?: boolean;
  autoRowHeight?: boolean;
  disablePointer?: boolean;
  gridId?: string;
  showClearAllFilter?: boolean;
  gridBorderColor?: string;
  gridHighlighColor?: string;
  paginationIconColor?: string;
  clearAllFilterBtnSx?: SxProps<Theme>;
  rowsPerPageOptions?: number[];
  defaultSortModel?: GridSortModel;
}

interface IGridRowContext {
  errors: { [key: string]: string | undefined };
  resetError: (field: string) => void;
  resetErrors: () => void;
}

export const GridRowContext = createContext<IGridRowContext>({
  errors: {},
  resetError: () => null,
  resetErrors: () => null,
});

export const PhoenixGrid = ({
  name = '',
  titleColor,
  height,
  strict,
  rows,
  columns,
  actions,
  pageSize = 10,
  rowsPerPageOptions,
  withHeader,
  subtitle,
  disabled,
  canNotEdit,
  canNotCopy,
  noButton,
  buttonTitle,
  disablePointer = false,
  onButtonClick,
  headerContent,
  withSubheader,
  newRowModel,
  initialState,
  sortModel,
  onSortModelChange,
  pinnedLeft,
  pinnedRight,
  withoutConfirmation,
  btn,
  rowActionsSlot,
  validateValues,
  validateSchema,
  isFullHeightColumn,
  hideFooter,
  onFilterModelChange,
  gridBorderColor,
  onPageChange,
  isFlexible = false,
  autoRowHeight = false,
  gridId,
  showClearAllFilter = false,
  clearAllFilterBtnSx,
  titleFontWeight,
  gridHighlighColor = '',
  paginationIconColor = '',
  defaultSortModel,
  //paginationMode = 'client',
  //rowCount = 0,
  ...props
}: IPhoenixGridProps) => {
  const gridInitialState = {
    filter: {
      filterModel: {
        items: [],
      },
    },
    pagination: {
      page: 0,
      pageSize: pageSize,
    },
    sorting: {
      sortModel: [],
    },
  };
  const gridName = gridId || name;
  const apiRef = useGridApiRef();
  const dispatch = useDispatch();
  const savedState = useAppSelector(selectGridState(gridName));
  const ref = props?.apiRef || apiRef;
  const [editableRowError, setEditableRowErrors] = useState({});
  const [editableRowId, setEditableRowId] = useState<GridRowId | null>(props?.editableRowId || null);
  const [page, setPage] = useState(0);
  const [currentPageSize, setCurrentPageSize] = useState(pageSize);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [forceUpdate, setForceUpdate] = useState(0);
  const [validationAlert, setValidationAlert] = useState({
    open: false,
    title: '',
    alertMessages: [] as IAlertMessage[],
  });
  //the default sort column
  const defaultSort = defaultSortModel?.[0];
  
  const isAddingRow = useMemo(() => editableRowId === newGridRecordId, [editableRowId]);

  const handleResetError = useCallback((field: string) => {
    setEditableRowErrors((prevState) => ({ ...prevState, [field]: undefined }));
  }, []);
  
  const handleResetErrors = useCallback(() => setEditableRowErrors({}), []);

  const handleAddRow = useCallback(() => {
    if (editableRowId !== null) {
      handleResetErrors();
      ref.current?.stopRowEditMode({ id: editableRowId, ignoreModifications: true });
    }

    if (!isAddingRow) {
      ref.current.updateRows([{ ...newGridRecord, ...newRowModel }]);
      setTimeout(() => {
        ref.current?.startRowEditMode({ id: newGridRecordId });
        ref.current?.setSelectionModel([newGridRecordId]);
        setEditableRowId(newGridRecordId);
        navigateToLastGridRow(ref.current, pageSize);
      });
    }
  }, [editableRowId, handleResetErrors, isAddingRow]);

  const updatedColumns = useMemo(
    () =>
      columns.map((col) => {
        let updatedCol = col;
  
        if (col.type === 'number') {
          updatedCol = {
            ...col,
            filterOperators: getGridNumericOperators().filter(
              (operator) =>
                operator.value === '>' ||
                operator.value === '<' ||
                operator.value === '=' ||
                operator.value === '<=' ||
                operator.value === '>='
            ),
          };
        } else if (col.type === 'date' || col.type === 'dateTime') {
          updatedCol = {
            ...col,
            filterOperators: getGridDateOperators().filter(
              (operator) =>
                operator.value === 'is' ||
                operator.value === 'after' ||
                operator.value === 'before' ||
                operator.value === 'onOrAfter' ||
                operator.value === 'onOrBefore'
            ),
          };
        } else if (col.type === 'boolean') {
          updatedCol = {
            ...col,
            filterOperators: getGridBooleanOperators(),
          };
        } else {
          updatedCol = {
            ...col,
            filterOperators: getGridStringOperators().filter(
              (operator) =>
                operator.value === 'contains' ||
                operator.value === 'equals'
            ),
          };
        }
  
        // Default sort column must never enter MUI's "clear" state.
        if (defaultSort?.field === col.field) {
          updatedCol = {
            ...updatedCol,
            sortingOrder:
              defaultSort.sort === 'desc'
                ? ['desc', 'asc']
                : ['asc', 'desc'],
          };
        }
  
        return updatedCol;
      }),
    [columns, defaultSort]
  );

  useEffect(() => {
    const currentState = ref.current?.exportState();

    if (savedState) {
      const { ...restSavedState } = savedState;

      ref.current?.restoreState({
        ...currentState,
        ...restSavedState,
      });
    } else {
      ref.current?.restoreState({
        ...currentState,
        ...gridInitialState,
      });
    }
  }, [gridName]);

  useEffect(() => {
    const handleZoom = () => {
      setForceUpdate(prev => prev + 1);
    };
    
    window.addEventListener('resize', handleZoom);
    return () => window.removeEventListener('resize', handleZoom);
  }, []);

  const processSaveRow = useCallback(
    async(row: GridRowModel) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response: any = await actions?.onSave?.(row);
      
      if (!response?.error) {
        ref.current.stopRowEditMode({ id: row.id, ignoreModifications: true });
        handleResetErrors();
        setEditableRowId(null);
        return row;
      }
    },
    [actions?.onSave]
  );

  const validateIfValidationExists = useCallback((row: GridRowModel) => {
    try {
      validateSchema?.validateSync(row, { abortEarly: false });
    } catch (error) {
      const errors = (error as { inner: ValidationError[] }).inner;

      const result = formatValidation(errors);

      setEditableRowErrors(result);

      return false;
    }

    if (validateValues) {
      return validateValues(row);
    }

    setEditableRowErrors({});

    return true;
  }, []);

  const showValidationAlert = useCallback((errors: Record<string, string>) => {
    const messages = [...new Set(Object.values(errors).filter(Boolean))];
  
    setValidationAlert({
      open: true,
      title: 'Validation Error',
      alertMessages: [
        {
          subtitle: '',
          listItem: messages,
        },
      ],
    });
  }, []);

  const processRowUpdate = useCallback(
    async (newRow: GridValidRowModel) => {
      const validationResult = validateIfValidationExists(newRow);
      if (validationResult) {
        const result = await processSaveRow(newRow);
        return result ?? newRow;
      }
      try {
        await validateSchema?.validate(newRow, {
          abortEarly: false,
        });
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = formatValidation(err.inner);

          setEditableRowErrors(errors);
          showValidationAlert(errors);
  
          throw new Error('Validation failed');
        }
      }
  
      throw new Error('Validation failed');
    },
    [processSaveRow, validateIfValidationExists, validateSchema, showValidationAlert]
  );

  const onSaveClick = useCallback((row: GridRowModel) => {
    ref.current.stopRowEditMode({ id: row.id });
    setEditableRowId(null);
  }, [editableRowId]);

  const gridColActions = useMemo(
    () => ([
      dataGridCol('hidden', {
        hide: true,
        ...gridBooleanCell
      }),
      {
        minWidth: 105,
        field: 'options',
        headerName: 'Actions',
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params: GridRenderCellParams) => (
          <GridColActions
            ref={ref}
            name={name}
            params={params}
            isRowValid={true}
            canNotCopy={canNotCopy}
            canNotEdit={canNotEdit}
            rowActionsSlot={rowActionsSlot}
            setEditableRowId={setEditableRowId}
            editableRowId={editableRowId}
            withoutConfirmation={withoutConfirmation}
            pageSize={pageSize}
            onSaveClick={() => onSaveClick(params.row)}
            onCancelClick={actions?.onCancel}
            onCopyClick={actions?.onCopy}
            onEditClick={actions?.onEdit}
            onViewClick={actions?.onView}
            onDeleteClick={actions?.onDelete}
          />
        ),
      }]),
    [editableRowId, name, pageSize, withoutConfirmation, rowActionsSlot, actions, onSaveClick]
  );

  const handleSaveGridState = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (params: any) => {
      const gridState = {
        filter: {
          filterModel: params.filter.filterModel,
        },
        pagination: params.pagination,
        sorting: {
          sortModel: params.sorting.sortModel,
        },
      };
      const isStateChanged = !_.isEqual(gridState, savedState);

      if (gridName && isStateChanged) {
        dispatch(updateGridState({ gridName, gridState }));
      }
    },
    [gridName, savedState]
  );

  const handleApply = useCallback(() => {
    ref.current.hideFilterPanel();
  }, []);

  const onRowEditStop = useCallback((params: GridValidRowModel, event: MuiEvent ) => {
    if ([GridCellEditStopReasons.enterKeyDown, GridCellEditStopReasons.cellFocusOut, GridCellEditStopReasons.tabKeyDown].includes(params.reason)) {
      event.defaultMuiPrevented = true;
    }
  }, []);

  const handlePageChange = useCallback((page: number, details: GridCallbackDetails<any>) => {
    onPageChange?.(page, details);
    setPage(page);
  }, [onPageChange]);

  useEffect(() => {
    if (!rowsPerPageOptions) { 
      setCurrentPageSize(pageSize);
    } else {
      setCurrentPageSize(currentPageSize);
    }
  }, [pageSize, currentPageSize, rowsPerPageOptions]);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    if (!rowsPerPageOptions) { 
      setCurrentPageSize(pageSize);
    } else {
      setCurrentPageSize(newPageSize);
    }
  }, [pageSize, rowsPerPageOptions]);

  const handleFilterModelChange = useCallback((filterModel: GridFilterModel, details: GridCallbackDetails<'filter'>) => {
    onFilterModelChange?.(filterModel, details);
    setFilterModel(filterModel);
    // Reset the pagination to the first page (page index 0)
    if (details.reason) {
      handlePageChange(0, {});
    }
  }, [onFilterModelChange, handlePageChange]);

  const handleClearAll = useCallback(() => {
    const newFilterModel = { items: [] };
    const newDetails = { reason: 'ClearFilter' } as GridCallbackDetails;

    handleFilterModelChange(newFilterModel, newDetails);
    handleApply();
  }, [handleFilterModelChange, handleApply]);
  
  const cols = useMemo(() => (actions ? [...updatedColumns, ...gridColActions] : updatedColumns), [actions, gridColActions, updatedColumns]);

  return (
    <>
      {withHeader && (
        <PhoenixGridHeader
          buttonWidth="auto"
          showClearFilter={showClearAllFilter}
          handleClearAll={handleClearAll}
          clearAllFilterBtnSx={clearAllFilterBtnSx}
          disableFilterBtn={!filterModel.items.length}
          disabled={disabled || isAddingRow}
          titleFontWeight={titleFontWeight}
          title={name && `${capitalize(name) + (strict ? '' : 's')}`}
          subtitle={subtitle}
          titleColor={titleColor}
          buttonTitle={noButton ? '' : buttonTitle || `Add New ${capitalize(name)}`}
          btn={btn}
          onButtonClick={onButtonClick || handleAddRow}
        >
          {headerContent}
        </PhoenixGridHeader>
      )}

      {withSubheader}

      <GridWrapper
        height={height}
        gridHighlighColor={gridHighlighColor}
        enabled={!!height || !rows?.length}
        isFullHeightColumn={isFullHeightColumn}
        hideFooter={hideFooter}
        isFlexible={isFlexible}
        gridBorderColor={gridBorderColor}
        disablePointer={disablePointer}
      >
        <GridRowContext.Provider
          value={{
            errors: editableRowError,
            resetError: handleResetError,
            resetErrors: handleResetErrors,
          }}
        >
          <DataGridPro
            {...props}
            key={forceUpdate}
            rows={rows || []}
            apiRef={ref}
            columns={cols}
            rowHeight={40}
            headerHeight={40}
            experimentalFeatures={{ newEditingApi: true }}
            sortModel={sortModel}
            processRowUpdate={processRowUpdate}
            getRowId={props?.getRowId || ((row) => row?.id)}
            rowsPerPageOptions={rowsPerPageOptions || [pageSize]}
            filterModel={filterModel}
            page={page}
            pageSize={currentPageSize}
            initialState={{
              ...initialState,
            }}
            pinnedColumns={{
              left: pinnedLeft ?? [],
              right: (pinnedRight ?? []).concat(['options'])
            }}
            {...(autoRowHeight && { getRowHeight: () => 'auto' })}
            components={{
              FilterPanel: CustomGridFilterPanel,
              Pagination: GridPagination
            }}
            componentsProps={{
              filterPanel: { handleApply, handleClearAll },
              pagination: { paginationIconBgColor: gridHighlighColor, paginationIconColor, rowsPerPageOptions: rowsPerPageOptions || [pageSize] }
            }}
            onPageSizeChange={handlePageSizeChange}
            onProcessRowUpdateError={(error) => console.error(error)}
            onFilterModelChange={handleFilterModelChange}
            onRowEditStop={onRowEditStop}
            onPageChange={handlePageChange}
            onStateChange={handleSaveGridState}
            onSortModelChange={onSortModelChange}
          />
        </GridRowContext.Provider>
      </GridWrapper>
      {validationAlert.open && (
        <AlertModal
          open
          hideCancel
          buttonText="Ok"
          title={validationAlert.title}
          alertMessages={validationAlert.alertMessages}
          onConfirm={() =>
            setValidationAlert(prev => ({
              ...prev,
              open: false,
            }))
          }
          onClose={() =>
            setValidationAlert(prev => ({
              ...prev,
              open: false,
            }))
          }
        />
      )}
    </>
  );
};
